import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, ArrowRight, Star, BookOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { LLink as Link } from './LocalizedLink';
import { useI18n } from '../context/I18nContext';

// Small badge showing the English-support level.
function SupportBadge({ level, tKey }) {
  const map = {
    excellent: ['bg-emerald-100 text-emerald-800', 'Excellent'],
    good:      ['bg-emerald-50 text-emerald-700',  'Good'],
    limited:   ['bg-amber-50 text-amber-700',      'Limited'],
    none:      ['bg-rose-50 text-rose-700',        'None'],
    unknown:   ['bg-slate-100 text-slate-600',     'Unknown'],
  };
  const [cls, defaultLabel] = map[level] || map.unknown;
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${cls}`}>
      🇬🇧 {tKey ? tKey(defaultLabel) : defaultLabel}
    </span>
  );
}

/**
 * Renders one insurer quote row: price on the right, name + trust badges,
 * top pros/cons, expandable long-form review.
 *
 * Props:
 *  - quote: { insurer, premium, premium_monthly?, rating, highlights[] }
 *  - profile: nullable insurer profile (from /api/insurers/lookup)
 *  - isBest: boolean — first (cheapest) row gets a highlight
 */
export default function InsurerCard({ quote, profile, isBest, currency = '€' }) {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();

  // Show up to 3 pros / 1 con by default, expandable to full lists.
  const pros = profile?.pros || [];
  const cons = profile?.cons || [];
  const shortPros = pros.slice(0, 3);
  const shortCons = cons.slice(0, 1);
  const hasEditorial = pros.length > 0 || cons.length > 0 || profile?.editor_notes;
  const supportLevel = profile?.english_support;
  const supportNote = profile?.english_support_note;
  const supportLabels = {
    excellent: t('insurer.support_excellent', 'Excellent — native English support'),
    good:      t('insurer.support_good',      'Good — English-speaking staff available'),
    limited:   t('insurer.support_limited',   'Limited — mostly Spanish, some English'),
    none:      t('insurer.support_none',      'None — Spanish only'),
    unknown:   t('insurer.support_unknown',   'Support level not yet verified'),
  };
  const supportColors = {
    excellent: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    good:      'bg-emerald-50 border-emerald-200 text-emerald-700',
    limited:   'bg-amber-50 border-amber-200 text-amber-800',
    none:      'bg-rose-50 border-rose-200 text-rose-800',
    unknown:   'bg-slate-50 border-slate-200 text-slate-700',
  };

  return (
    <div className={`border rounded-2xl p-5 transition-shadow ${isBest ? 'border-emerald-300 bg-emerald-50/40 shadow-sm' : 'border-slate-200 bg-white'}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        {/* Left: insurer + trust signals + pros/cons */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            {profile?.logo_url ? (
              <img
                src={profile.logo_url}
                alt={`${profile.display_name || quote.insurer} logo`}
                className="w-12 h-12 object-contain rounded border border-slate-200 bg-white p-1 shrink-0"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <span className="w-12 h-12 rounded border border-slate-200 bg-slate-100 flex items-center justify-center text-slate-400 shrink-0 text-[10px] font-semibold uppercase">
                {(quote.insurer || '?').slice(0, 3)}
              </span>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[#0b1f3a] text-lg">{profile?.display_name || quote.insurer}</span>
                {isBest && (
                  <span className="text-[10px] uppercase font-semibold tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                    {t('wizard.best_price', 'Best price')}
                  </span>
                )}
              </div>
              {profile?.tagline && (
                <p className="text-xs text-slate-500 mt-0.5 truncate">{profile.tagline}</p>
              )}
            </div>
          </div>

          {/* Trust row (star rating + AM Best only; English support has its own row) */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 text-amber-400">
              {[0, 1, 2, 3, 4].map((s) => (
                <Star key={s} size={13} fill={s < Math.round(quote.rating) ? 'currentColor' : 'none'} />
              ))}
              <span className="text-xs text-slate-500 ml-1">{quote.rating?.toFixed?.(1) ?? quote.rating}</span>
            </div>
            {profile?.am_best_rating && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                AM Best {profile.am_best_rating}
              </span>
            )}
          </div>

          {/* Quote highlights (from Avant2 / mock) */}
          {(quote.highlights || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {quote.highlights.map((h) => (
                <span key={h} className="text-[11px] bg-slate-100 text-slate-700 px-2 py-1 rounded-md">{h}</span>
              ))}
            </div>
          )}

          {/* Pros / cons (only if published) */}
          {hasEditorial && (
            <div className="mt-4 space-y-1.5">
              {shortPros.map((p) => (
                <p key={p} className="flex items-start gap-2 text-sm text-slate-700">
                  <ThumbsUp size={13} className="text-emerald-600 mt-0.5 shrink-0" /> {p}
                </p>
              ))}
              {shortCons.map((c) => (
                <p key={c} className="flex items-start gap-2 text-sm text-slate-700">
                  <ThumbsDown size={13} className="text-rose-500 mt-0.5 shrink-0" /> {c}
                </p>
              ))}
            </div>
          )}

          {/* English support row — always shown when we know the level */}
          {supportLevel && (
            <div className={`mt-4 border rounded-lg px-3 py-2 text-sm ${supportColors[supportLevel] || supportColors.unknown}`}>
              <p className="font-semibold flex items-center gap-2">
                <span aria-hidden>🇬🇧</span>
                <span>{t('insurer.support_row_label', 'English support')}: {supportLabels[supportLevel] || supportLabels.unknown}</span>
              </p>
              {supportNote && (
                <p className="mt-1 text-xs opacity-90 leading-relaxed">{supportNote}</p>
              )}
            </div>
          )}

          {/* Read the full article link — only when this insurer has a
              published long-form review at /insurers/{slug} */}
          {profile?.article_published && profile?.slug && (
            <Link
              to={`/insurers/${profile.slug}`}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0d3a8a] hover:underline"
              data-testid={`insurer-read-article-${profile.slug}`}
            >
              <BookOpen size={14} />
              {t('insurer.read_about', 'Read about')} {profile.display_name || quote.insurer}
              <ArrowRight size={13} />
            </Link>
          )}

          {/* Expand link */}
          {(pros.length > 3 || cons.length > 1 || profile?.editor_notes) && (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="text-[#0d3a8a] text-sm font-semibold inline-flex items-center gap-1 mt-3"
              data-testid={`insurer-toggle-${profile.slug || quote.insurer}`}
            >
              {open ? (
                <>{t('insurer.hide_review', 'Hide details')} <ChevronUp size={14} /></>
              ) : (
                <>{t('insurer.read_review', 'Read full review')} <ChevronDown size={14} /></>
              )}
            </button>
          )}
          {open && (
            <div className="mt-3 pt-3 border-t border-slate-200 space-y-3">
              {pros.length > 3 && (
                <div>
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-1">
                    {t('insurer.more_pros', 'More pros')}
                  </p>
                  {pros.slice(3).map((p) => (
                    <p key={p} className="flex items-start gap-2 text-sm text-slate-700">
                      <ThumbsUp size={13} className="text-emerald-600 mt-0.5 shrink-0" /> {p}
                    </p>
                  ))}
                </div>
              )}
              {cons.length > 1 && (
                <div>
                  <p className="text-xs font-semibold text-rose-700 uppercase tracking-wider mb-1">
                    {t('insurer.more_cons', 'More cons')}
                  </p>
                  {cons.slice(1).map((c) => (
                    <p key={c} className="flex items-start gap-2 text-sm text-slate-700">
                      <ThumbsDown size={13} className="text-rose-500 mt-0.5 shrink-0" /> {c}
                    </p>
                  ))}
                </div>
              )}
              {profile?.editor_notes && (
                <div className="prose prose-sm max-w-none text-slate-700">
                  <ReactMarkdown>{profile.editor_notes}</ReactMarkdown>
                </div>
              )}
            </div>
          )}

          {/* Graceful hint when no profile at all (e.g. brand-new insurer from Avant2) */}
          {!profile && (
            <p className="mt-3 text-xs text-slate-400 italic">
              {t('insurer.profile_soon', 'Full company profile coming soon.')}
            </p>
          )}
        </div>

        {/* Right: price + CTA */}
        <div className="md:text-right md:w-52 shrink-0 border-t md:border-t-0 md:border-l border-slate-100 md:pl-4 pt-4 md:pt-0">
          {quote.premium_monthly ? (
            <>
              <p className="text-3xl font-bold text-[#0b1f3a]">
                {currency}{quote.premium_monthly}
                <span className="text-sm font-medium text-slate-500">{t('wizard.per_month', '/month')}</span>
              </p>
              <p className="text-xs text-slate-500">≈ {currency}{quote.premium}{t('wizard.per_year', '/year')}</p>
            </>
          ) : (
            <p className="text-3xl font-bold text-[#0b1f3a]">
              {currency}{quote.premium}
              <span className="text-sm font-medium text-slate-500">{t('wizard.per_year', '/year')}</span>
            </p>
          )}
          <button className="btn-primary mt-2 w-full justify-center">
            {t('wizard.accept_quote', 'Accept quote')} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
